import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Select, DatePicker, message } from "antd";
import dayjs from "dayjs";
import { INVENTORY_WASTAGE_TYPE, INVENTORY_WASTAGE_REASON, INGREDIENT_UNITS } from "../../../constant/constants";
import ApiService from "../../../utils/ApiService";
import useUserStore from "../../../stores/store";
import CustomLoader from "../../menu/CustomLoader";
import { getIngredientUnitLabel } from "../../../services/inventory/inventory.service";
import CustomInputNumber from "../../CustomInputNumber";
import { getParsedNumber, roundNumber } from "../../../utils/Common";

export default function WastageAddEditComponent({ editMode = false, data = [] }) {
  const { restaurantId, user, showNotification, setNetworkCall } = useUserStore();
  const navigate = useNavigate();
  const [wastageType, setWastageType] = useState();
  const [wastage, setWastage] = useState();
  const [wastageUnit, setWastageUnit] = useState("");
  const [costPerUnit, setCostPerUnit] = useState();
  const [totalAmount, setTotalAmount] = useState();
  const [wastageReason, setWastageReason] = useState();
  const [date, setDate] = useState();

  const [ingredientPerItemOptions, setIngredientPerItemOptions] = useState();
  const [ingredientPerItem, setIngredientPerItem] = useState();

  const [foodList, setFoodList] = useState([]);
  const [selectedFoodId, setSelectedFoodId] = useState();

  const [adOnsList, setAdOnsList] = useState([]);
  const [selectedAdOnsId, setSelectedAdOnsId] = useState();

  const [foodOptions, setFoodOptions] = useState([]);
  const [selectedFoodOptionId, setSelectedFoodOptionId] = useState();
  const [isVariantRequired, setIsVariantRequired] = useState(false);

  // Effect to load dropdown options whenever wastageType changes
  useEffect(() => {
    if (wastageType === "FOOD") {
      fetchFoodList();
    } else if (wastageType === "ADD_ONS") {
      fetchAdOnsList();
    } else {
      fetchIngredientItems();
    }
  }, [wastageType]);

  useEffect(() => {
    if (selectedFoodId) {
      const selectedFood = foodList.find((item) => item.value === selectedFoodId);
      if (selectedFood?.foodOptions) {
        setFoodOptions(
          selectedFood.foodOptions.map((opt) => ({
            label: ["System Default", "Default"].includes(opt.optionName) ? "" : opt.optionName,
            value: opt.optionId,
            costPerUnit: opt.costPerUnit,
            price: opt.price,
          }))
        );
      } else {
        setFoodOptions([]);
      }
    } else {
      setFoodOptions([]);
    }
  }, [selectedFoodId, foodList]);

  useEffect(() => {
    if (selectedAdOnsId) {
      const selectedAdOn = adOnsList.find((item) => item.value === selectedAdOnsId);
      if (selectedAdOn?.foodOptions) {
        setFoodOptions(
          selectedAdOn.foodOptions.map((opt) => ({
            label: ["System Default", "Default"].includes(opt.optionName) ? "" : opt.optionName,
            value: opt.optionId,
            costPerUnit: opt.costPerUnit,
            price: opt.price,
          }))
        );
      } else {
        setFoodOptions([]);
      }
    } else {
      setFoodOptions([]);
    }
  }, [selectedAdOnsId, adOnsList]);

  // Fetch ingredients from API and format options for Select component
  async function fetchIngredientItems() {
    try {
      const res = await ApiService.GET_INVENTORY_INGREDIENT_DROPDOWN({ restaurantId }, user.token.token);
      if (res.code === 200) {
        const records = res.payload.records;
        setIngredientPerItemOptions(
          records.map((item) => ({
            label: item.name,
            value: item.id,
            baseUnit: item.baseUnit,
            costPerUnit: item.baseUnitPrice,
          }))
        );
      }
    } catch (err) {
      // handle error
    }
  }

  // Fetch food list from API and format options for Select component
  async function fetchFoodList() {
    try {
      const res = await ApiService.GET_INVENTORY_FOOD_ITEM_DROPDOWN({ restaurantId }, user.token.token);
      if (res.code === 200) {
        const records = res.payload.records;
        const formatted = records.map((item) => ({
          label: item.name,
          value: item.foodId,
          foodOptions: item.options,
        }));
        setFoodList(formatted);
      }
    } catch (err) {
      // handle error
    }
  }

  // Fetch AdOns list from API and format options for Select component
  async function fetchAdOnsList() {
    try {
      const res = await ApiService.GET_INVENTORY_ADONS_ITEM_DROPDOWN({ restaurantId }, user.token.token);
      if (res.code === 200) {
        const records = res.payload.records;
        const formatted = records.map((item) => ({
          label: item.name,
          value: item.addOnId,
          foodOptions: item.options,
        }));
        setAdOnsList(formatted);
      }
    } catch (err) {
      // handle error
    }
  }

  function onHandleWastageType(val) {
    setWastageType(val);

    // ✅ Reset food-related states
    setSelectedFoodId(undefined);
    setSelectedFoodOptionId(undefined);
    setSelectedFoodOptionId(undefined);
    setFoodList([]);
    setFoodOptions([]);
    setAdOnsList([]);
    setWastageUnit("");
    setCostPerUnit();
  }

  function onHandleSelectedFoodId(val) {
    setSelectedFoodId(val);

    // ✅ Reset food options related states
    setSelectedFoodOptionId(undefined);
    setFoodOptions([]);
  }

  function onHandleSelectedAdOnsId(val) {
    setSelectedAdOnsId(val);

    // ✅ Reset food options related states
    setSelectedFoodOptionId(undefined);
    setFoodOptions([]);
  }

  function onHandleIngredientPerItem(val) {
    setIngredientPerItem(val);

    // set wastage unit
    const selectedIngredient = ingredientPerItemOptions.find((item) => item.value === val);
    const baseUnit = selectedIngredient?.baseUnit || "";
    setWastageUnit(baseUnit);
    setCostPerUnit(selectedIngredient.costPerUnit);
  }

  function onHandleSetSelectedFoodOptionId(val) {
    setSelectedFoodOptionId(val);
    // set costPerUnit
    const selectedFood = foodOptions.find((item) => item.value === val);

    setCostPerUnit(selectedFood.costPerUnit);
  }

  function onHandleWastage(qty) {
    setWastage(qty);

    // calculate total amount
    setTotalAmount(roundNumber(getParsedNumber(qty) * getParsedNumber(costPerUnit)));
  }

  function onHandleWastageUnit(val) {
    setWastageUnit(val);
  }

  function onHandleCostPerUnit(e) {
    setCostPerUnit(e.target.value);
  }

  function onHandleWastageReason(val) {
    setWastageReason(val);
  }

  function onHandleDate(date, dateString) {
    setDate(dateString);
  }

  function onHandleCancel() {
    return navigate("/inventory/wastage");
  }

  function setWastItemId(wastageType) {
    const map = {
      INGREDIENT: ingredientPerItem,
      FOOD: selectedFoodId,
      ADD_ONS: selectedAdOnsId,
    };
    return map[wastageType] || null;
  }

  function validateWastageData(wastageData) {
    if (!wastageData.restaurantId || wastageData.restaurantId === "") {
      message.warning(
        <p className="font-p_regular text-[12px]">
          Please select a <span className="text-red-500">restaurant</span>.
          <br />
        </p>
      );
      return false;
    }

    if (!wastageData.wasteType || wastageData.wasteType === "") {
      message.warning(
        <p className="font-p_regular text-[12px]">
          Please select a waste <span className="text-red-500">type</span>.
        </p>
      );
      return false;
    }
    // INVENTORY_WASTAGE_TYPE
    if (!wastageData.wasteItemId || wastageData.wasteItemId === "") {
      message.warning(
        <p className="font-p_regular text-[12px]">
          Please select a waste <span className="text-red-500">item</span>.
        </p>
      );
      return false;
    }

    // When wastage type is Food
    if (
      wastageData.wasteType === INVENTORY_WASTAGE_TYPE[1].value &&
      (!wastageData.wasteItemOptionId || wastageData.wasteItemOptionId === "")
    ) {
      message.warning(
        <p className="font-p_regular text-[12px]">
          Please select wastage food item <span className="text-red-500">option / variation</span>.
        </p>
      );
      return false;
    }

    if (
      wastageData.wasteQuantity === undefined ||
      wastageData.wasteQuantity === null ||
      isNaN(wastageData.wasteQuantity)
    ) {
      message.warning(
        <p className="font-p_regular text-[12px]">
          Please enter a valid waste <span className="text-red-500">quantity</span>.
        </p>
      );
      return false;
    }

    if (Number(wastageData.wasteQuantity) <= 0) {
      message.warning(
        <p className="font-p_regular text-[12px]">
          Waste <span className="text-red-500">quantity</span> must be greater than zero.
        </p>
      );
      return false;
    }

    if (!wastageData.wasteReason || wastageData.wasteReason.trim() === "") {
      message.warning(
        <p className="font-p_regular text-[12px]">
          Please provide a <span className="text-red-500">reason</span> for the waste.
        </p>
      );
      return false;
    }

    if (!wastageData.wasteDate || wastageData.wasteDate === "") {
      message.warning(
        <p className="font-p_regular text-[12px]">
          Please select the waste <span className="text-red-500">date</span>.
        </p>
      );
      return false;
    }

    if (!wastageData.unit || wastageData.unit === "") {
      message.warning(
        <p className="font-p_regular text-[12px]">
          Please select the <span className="text-red-500">unit</span>.
        </p>
      );
      return false;
    }

    return true;
  }

  function onHandleSave() {
    setNetworkCall(true);
    const wastageData = {
      restaurantId: restaurantId,
      wasteType: wastageType,
      wasteItemId: setWastItemId(wastageType),
      wasteItemOptionId: selectedFoodOptionId,
      wasteQuantity: wastage,
      wasteReason: wastageReason,
      wasteDate: date,
      unit: wastageUnit || "P",
    };

    if (!validateWastageData(wastageData)) {
      setNetworkCall(false);
      return;
    }
    wastageAdd(wastageData);
  }

  async function wastageAdd(payload) {
    try {
      const res = await ApiService.INVENTORY_WASTAGE_ADD(payload, user.token.token);
      if (res.code !== 200) {
        showNotification(res.data.message);
        setNetworkCall(false);
        return;
      }
      setNetworkCall(false);
      showNotification(`wastage successfully created.`, false);
      return navigate("/inventory/wastage");
    } catch (err) {
      showNotification(err.response.data.message);
      setNetworkCall(false);
      return;
    }
  }

  useEffect(() => {
    setIsVariantRequired(true);
    onHandleWastage("");

    let targetSelectedId = "";

    if (wastageType === "FOOD") {
      targetSelectedId = selectedFoodId;
    } else if (wastageType === "ADD_ONS") {
      targetSelectedId = selectedAdOnsId;
    }

    if (!Boolean(wastageType) || !Boolean(targetSelectedId) || !["FOOD", "ADD_ONS"].includes(wastageType)) {
      setSelectedFoodOptionId("");
      return;
    }

    if (foodOptions.length > 1) {
      return;
    }

    if (foodOptions.length === 1 && foodOptions[0].label === "") {
      setIsVariantRequired(false);
      onHandleSetSelectedFoodOptionId(foodOptions[0].value);
    }
  }, [wastageType, selectedFoodId, selectedAdOnsId, foodOptions]);

  return (
    <section className="w-full h-full px-[35px] pt-[50px] pb-[35px]">
      <CustomLoader />
      <form>
        <div className="flex items-center space-x-[22px] mb-[40px]">
          <div className="h-[48px] min-w-[297px]">
            <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
              Wastage Type <span className="text-red-600">*</span>
            </label>
            <Select
              value={wastageType}
              name="wastageType"
              style={{ width: "100%", height: "100%" }}
              onChange={onHandleWastageType}
              options={INVENTORY_WASTAGE_TYPE}
              placeholder="Wastage Type"
            />
          </div>
          {/* Conditionally render Select dropdown based on wastageType */}
          <div className="h-[48px] min-w-[297px]">
            {wastageType === "INGREDIENT" && (
              <>
                <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
                  Ingredient/Item <span className="text-red-600">*</span>
                </label>
                <Select
                  value={ingredientPerItem}
                  onChange={onHandleIngredientPerItem}
                  options={ingredientPerItemOptions}
                  placeholder="Select Ingredient/Item"
                  style={{ width: "100%", height: "100%" }}
                  allowClear
                />
              </>
            )}

            {wastageType === "FOOD" && (
              <>
                <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
                  Food Item <span className="text-red-600">*</span>
                </label>
                <Select
                  showSearch
                  value={selectedFoodId}
                  onChange={onHandleSelectedFoodId}
                  options={foodList}
                  placeholder="Select Food Item"
                  style={{ width: "100%", height: "100%" }}
                  allowClear
                  optionFilterProp="label"
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "").toLowerCase().localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                />
              </>
            )}

            {wastageType === "ADD_ONS" && (
              <>
                <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
                  AdOns Item <span className="text-red-600">*</span>
                </label>
                <Select
                  value={selectedAdOnsId}
                  onChange={onHandleSelectedAdOnsId}
                  options={adOnsList}
                  placeholder="Select AdOns Item"
                  style={{ width: "100%", height: "100%" }}
                  allowClear
                />
              </>
            )}

            {!wastageType && (
              <>
                <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
                  Item <span className="text-red-600">*</span>
                </label>
                <Select disabled placeholder="Select Item" style={{ width: "100%", height: "100%" }} allowClear />
              </>
            )}
          </div>
        </div>

        {/* Second dropdown: Only show if FOOD or ADD_ONS and food is selected and Its populated by selected food list items options */}
        {(wastageType === "FOOD" || wastageType === "ADD_ONS") && (selectedFoodId || selectedAdOnsId) && (
          <div className="flex items-center space-x-[22px] mb-[40px]">
            <div className="h-[48px] w-full">
              <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
                Food Option / Variation {isVariantRequired && <span className="text-red-600">*</span>}
              </label>
              <Select
                disabled={!isVariantRequired}
                value={selectedFoodOptionId}
                onChange={onHandleSetSelectedFoodOptionId}
                options={foodOptions}
                placeholder="Select Food Option"
                style={{ width: "100%", height: "100%" }}
                allowClear
              />
            </div>
          </div>
        )}

        <div className="flex items-center space-x-[22px] mb-[40px]">
          <div className="h-[48px] min-w-[297px]">
            <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
              Wastage Qty <span className="text-red-600">*</span>
            </label>
            <CustomInputNumber
              name="wastage"
              value={wastage}
              setValue={onHandleWastage}
              isDecimalAllowed={true}
              inputClasses="h-full"
              min={0}
              max={99999}
            />
          </div>

          <div className="h-[48px] min-w-[297px]">
            <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">Unit</label>
            <div
              className="w-full h-full px-3 flex items-center border border-[#d9d9d9] rounded-[6px] bg-[#ffffff] text-[#000000d9] bg-[#F5F5F5]"
              style={{ fontSize: "14px" }}
            >
              {getIngredientUnitLabel(wastageUnit) || "Nos"}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-[22px] mb-[40px]">
          <div className="h-[48px] min-w-[297px]">
            <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">Cost/Unit (TK)</label>
            <div
              className="w-full h-full px-3 flex items-center border border-[#d9d9d9] rounded-[6px] bg-[#ffffff] text-[#000000d9] bg-[#F5F5F5]"
              style={{ fontSize: "14px" }}
            >
              {costPerUnit}
            </div>
          </div>

          <div className="h-[48px] min-w-[297px]">
            <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
              Wastage reason <span className="text-red-600">*</span>
            </label>
            <Select
              value={wastageReason}
              name="wastageReason"
              style={{ width: "100%", height: "100%" }}
              onChange={onHandleWastageReason}
              options={INVENTORY_WASTAGE_REASON}
              placeholder="ex: Spoiled"
            />
          </div>
        </div>

        <div className="h-[48px] w-full mb-[40px]">
          <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">Total Wastage amount (TK)</label>
          <div
            className="w-full h-full px-3 flex items-center border border-[#d9d9d9] rounded-[6px] bg-[#0000000a] text-[#000000d9]"
            style={{ fontSize: "14px" }}
          >
            {isNaN(totalAmount?.toFixed(2)) ? 0 : totalAmount?.toFixed(2)}
          </div>
        </div>

        <div className="h-[48px] w-full mb-[46px]">
          <label className="font-p_medium text-black leading-[13px] text-xs pb-[7px]">
            Date <span className="text-red-600">*</span>
          </label>
          <DatePicker
            className="h-full w-full"
            format="YYYY-MM-DD"
            showTime={false}
            placeholder="22 May 2025"
            value={date ? dayjs(date, "YYYY-MM-DD") : null}
            onChange={onHandleDate}
            disabledDate={(current) => {
              if (!current) return false;
              const today = dayjs().endOf("day");
              const sevenDaysAgo = dayjs().subtract(6, "day").startOf("day");
              return current < sevenDaysAgo || current > today;
            }}
          />
        </div>

        <div className="flex items-center space-x-[15px]">
          <button
            type="button"
            className="h-[42px] w-[100px] font-bold text-black font-base leading-[16px] rounded-[5px] bg-[#FCBD10]"
            onClick={onHandleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="h-[42px] w-[100px] font-bold text-white font-base leading-[16px] rounded-[5px] bg-black"
            onClick={onHandleSave}
          >
            Save
          </button>
        </div>
      </form>
    </section>
  );
}
